import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as routes from "@/constants/routes";
import { toV2Route } from "@/constants/routes";

interface UseNavigationHandlersParams {
  refreshProps: () => void;
}

interface UseNavigationHandlersResult {
  pressOnDashboard: () => void;
  pressOnMap: () => void;
  pressOnUserMenu: () => void;
  pressOnStationsMenu: () => void;
  pressOnTransactionsMenu: () => void;
  pressOnBikeMenu: () => void;
  pressOnLocksMenu: () => void;
  pressOnDockMenu: () => void;
  pressOnCitiesMenu: () => void;
  pressOnCouponsMenu: () => void;
  pressOnRewardsMenu: () => void;
  pressOnUserManagementMenu: () => void;
  pressOnReferralsMenu: () => void;
  pressOnNotificationsMenu: () => void;
  pressOnRentalsMenu: () => void;
  pressOnRentalRulesMenu: () => void;
  sidebarItemHandlers: Record<string, () => void>;
}

export function useNavigationHandlers({
  refreshProps,
}: UseNavigationHandlersParams): UseNavigationHandlersResult {
  const navigate = useNavigate();

  const pressOnDashboard = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_SCREEN));
  }, [refreshProps, navigate]);

  const pressOnMap = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_MAP));
  }, [refreshProps, navigate]);

  const pressOnUserMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_USERS));
  }, [refreshProps, navigate]);

  const pressOnStationsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_STATIONS));
  }, [refreshProps, navigate]);

  const pressOnTransactionsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_TRANSACTIONS));
  }, [refreshProps, navigate]);

  const pressOnBikeMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_BIKES));
  }, [refreshProps, navigate]);

  const pressOnLocksMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_LOCKS));
  }, [refreshProps, navigate]);

  const pressOnDockMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_DOCKS));
  }, [refreshProps, navigate]);

  const pressOnCitiesMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_CITIES));
  }, [refreshProps, navigate]);

  const pressOnCouponsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.COUPONS_ROUTE));
  }, [refreshProps, navigate]);

  const pressOnRewardsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.PROMOTIONS_REWARDS_ROUTE));
  }, [refreshProps, navigate]);

  const pressOnUserManagementMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.USER_MANAGEMENT_ROUTE));
  }, [refreshProps, navigate]);

  const pressOnReferralsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.REFERRAL_ROUTE));
  }, [refreshProps, navigate]);

  const pressOnNotificationsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.NOTIFICATIONS_ROUTE));
  }, [refreshProps, navigate]);

  const pressOnRentalsMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_RENTALS));
  }, [refreshProps, navigate]);

  const pressOnRentalRulesMenu = useCallback(() => {
    refreshProps();
    navigate(toV2Route(routes.DASHBOARD_RENTAL_RULES));
  }, [refreshProps, navigate]);

  const sidebarItemHandlers = useMemo(
    () => ({
      overview: pressOnDashboard,
      map: pressOnMap,
      bikes: pressOnBikeMenu,
      locks: pressOnLocksMenu,
      zones: pressOnStationsMenu,
      docks: pressOnDockMenu,
      rentals: pressOnRentalsMenu,
      fines: () => navigate(toV2Route(routes.DASHBOARD_FINES)),
      reservations: () => navigate(toV2Route(routes.DASHBOARD_RESERVATIONS)),
      "rental-rules": pressOnRentalRulesMenu,
      transactions: pressOnTransactionsMenu,
      users: pressOnUserMenu,
      "customer-restrictions": () =>
        navigate(toV2Route(routes.CUSTOMER_RESTRICTIONS_ROUTE)),
      coupons: pressOnCouponsMenu,
      referral: pressOnReferralsMenu,
      rewards: pressOnRewardsMenu,
      "topup-bonuses": () =>
        navigate(toV2Route(routes.PROMOTIONS_TOPUP_BONUSES_ROUTE)),
      "ride-pricing": () =>
        navigate(toV2Route(`${routes.PRICING_HUB_ROUTE}?tab=ride-pricing`)),
      "ride-passes": () =>
        navigate(toV2Route(`${routes.PRICING_HUB_ROUTE}?tab=ride-passes`)),
      cities: pressOnCitiesMenu,
      organizations: () => navigate(toV2Route(routes.DASHBOARD_ORGANIZATIONS)),
      "user-management": pressOnUserManagementMenu,
      "service-accounts": () =>
        navigate(toV2Route(routes.SERVICE_ACCOUNTS_ROUTE)),
      notifications: pressOnNotificationsMenu,
      analytics: () => navigate(toV2Route(routes.DASHBOARD_ANALYTICS)),
      "app-versions": () => navigate(toV2Route(routes.APP_VERSIONS_ROUTE)),
      "service-health": () => navigate(toV2Route(routes.SERVICE_HEALTH_ROUTE)),
      "api-docs": () => navigate(toV2Route(routes.API_DOCS_ROUTE)),
      "feature-flags": () => navigate(toV2Route(routes.FEATURE_FLAGS_ROUTE)),
      "analytics-v2": () => navigate(toV2Route(routes.DASHBOARD_ANALYTICS_V2)),
      "chat-analytics": () =>
        navigate(toV2Route(routes.DASHBOARD_CHAT_ANALYTICS)),
      partners: () => navigate(toV2Route(routes.DASHBOARD_PARTNERS)),
      "vehicle-models": () =>
        navigate(toV2Route(routes.DASHBOARD_VEHICLE_MODELS)),
    }),
    [
      pressOnDashboard,
      pressOnMap,
      pressOnBikeMenu,
      pressOnLocksMenu,
      pressOnStationsMenu,
      pressOnDockMenu,
      pressOnRentalsMenu,
      pressOnRentalRulesMenu,
      pressOnTransactionsMenu,
      pressOnUserMenu,
      pressOnCouponsMenu,
      pressOnRewardsMenu,
      pressOnUserManagementMenu,
      pressOnReferralsMenu,
      pressOnNotificationsMenu,
      pressOnCitiesMenu,
      navigate,
    ]
  );

  return {
    pressOnDashboard,
    pressOnMap,
    pressOnUserMenu,
    pressOnStationsMenu,
    pressOnTransactionsMenu,
    pressOnBikeMenu,
    pressOnLocksMenu,
    pressOnDockMenu,
    pressOnCitiesMenu,
    pressOnCouponsMenu,
    pressOnRewardsMenu,
    pressOnUserManagementMenu,
    pressOnReferralsMenu,
    pressOnNotificationsMenu,
    pressOnRentalsMenu,
    pressOnRentalRulesMenu,
    sidebarItemHandlers,
  };
}
